import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { HeroBanner } from '@/api'
import { cn } from '@/lib/cn'
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/ui/icons'

interface HeroCarouselProps {
  banners: HeroBanner[]
  loading?: boolean
}

const AUTOPLAY_MS = 6000

/**
 * Carrossel de banners no topo da home. Avança sozinho, pausa no hover e
 * respeita `prefers-reduced-motion` (a transição some via CSS global).
 */
export function HeroCarousel({ banners, loading }: HeroCarouselProps) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  const go = useCallback(
    (next: number) => {
      if (banners.length === 0) return
      setIndex(((next % banners.length) + banners.length) % banners.length)
    },
    [banners.length],
  )

  useEffect(() => {
    if (paused || banners.length <= 1) return
    const timer = setInterval(() => setIndex((i) => (i + 1) % banners.length), AUTOPLAY_MS)
    return () => clearInterval(timer)
  }, [paused, banners.length])

  if (loading) {
    return <div className="skeleton h-[190px] w-full sm:h-[260px] lg:h-[320px]" />
  }
  if (banners.length === 0) return null

  return (
    <div
      className="relative overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carrossel"
      aria-label="Destaques"
    >
      <div
        className="flex transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {banners.map((banner, bannerIndex) => {
          const image = (
            <picture>
              {banner.mobileImageUrl && (
                <source media="(max-width: 640px)" srcSet={banner.mobileImageUrl} />
              )}
              <img
                src={banner.imageUrl}
                alt={banner.alt}
                loading={bannerIndex === 0 ? 'eager' : 'lazy'}
                className="h-[190px] w-full object-cover sm:h-[260px] lg:h-[320px]"
              />
            </picture>
          )

          return (
            <div
              key={banner.id}
              className="w-full shrink-0"
              aria-hidden={bannerIndex !== index}
            >
              {banner.href ? <Link to={banner.href}>{image}</Link> : image}
            </div>
          )
        })}
      </div>

      {banners.length > 1 && (
        <>
          <HeroArrow direction="left" onClick={() => go(index - 1)} />
          <HeroArrow direction="right" onClick={() => go(index + 1)} />

          {/* Acima dos 40px em que o conteúdo da home invade o banner; mais
              baixo que isto, os indicadores ficariam escondidos atrás do card.
              O `z-20` os mantém clicáveis: o contêiner da home sobe com margem
              negativa e seu respiro transparente passa por cima daqui. */}
          <div className="absolute bottom-12 left-1/2 z-20 flex -translate-x-1/2 gap-2">
            {banners.map((banner, dotIndex) => (
              <button
                key={banner.id}
                type="button"
                onClick={() => go(dotIndex)}
                aria-label={`Ir para o banner ${dotIndex + 1}`}
                aria-current={dotIndex === index}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  dotIndex === index ? 'w-6 bg-white' : 'w-1.5 bg-white/60 hover:bg-white/85',
                )}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function HeroArrow({
  direction,
  onClick,
}: {
  direction: 'left' | 'right'
  onClick: () => void
}) {
  const Icon = direction === 'left' ? ChevronLeftIcon : ChevronRightIcon
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === 'left' ? 'Banner anterior' : 'Próximo banner'}
      className={cn(
        'absolute top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-black/20 hover:text-white',
        direction === 'left' ? 'left-2' : 'right-2',
      )}
    >
      <Icon className="size-7" strokeWidth={2.2} />
    </button>
  )
}
